<template>
    <div class="standings-table-wrapper">
        <table class="standings-table">
            <thead>
                <tr>
                    <th class="col-pos"></th>
                    <th class="col-team">Команда</th>
                    <th class="col-games">Игры</th>
                    <th class="col-wins">Победы</th>
                    <th class="col-points">Очки</th>
                    <th class="col-sets">Сеты</th>
                    <th class="col-losses">Поражения</th>
                    <th class="col-matches">Матчи</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="team in standings" :key="team.id" class="team-row">
                    <td class="col-pos">
                        <span class="position" :class="{
                            'last-place': maxPosition != null && team.position === maxPosition
                        }">
                            {{ team.position }}
                        </span>
                    </td>
                    <td class="col-team">
                        <component
                            :is="teamPublicPath(team) ? 'a' : 'div'"
                            :href="teamPublicPath(team) || undefined"
                            class="team-info"
                            :class="{ 'team-info--link': !!teamPublicPath(team) }"
                        >
                            <div class="team-logo" v-if="team.logo_path">
                                <img :src="team.logo_path" :alt="team.name" />
                            </div>
                            <div class="team-logo placeholder" v-else>
                                {{ team.name?.charAt(0) }}
                            </div>
                            <span class="team-name">{{ team.name }}</span>
                        </component>
                    </td>
                    <td class="col-games">{{ team.games_played }}</td>
                    <td class="col-wins"><span class="win-number">{{ team.wins }}</span></td>
                    <td class="col-points">
                        <span class="points">{{ team.points }}</span>
                    </td>
                    <td class="col-sets">
                        <div class="sets-score">
                            <span class="sets-won">{{ team.sets_won }}</span>
                            <span class="sets-sep">:</span>
                            <span class="sets-lost">{{ team.sets_lost }}</span>
                        </div>
                        <div class="sets-ratio">({{ formatRatio(team.sets_won, team.sets_lost) }})</div>
                    </td>
                    <td class="col-losses"><span class="loss-number">{{ team.losses }}</span></td>
                    <td class="col-matches">
                        <div class="matches-container">
                            <!-- Стрелка влево (всегда видима, но disabled когда нельзя прокрутить) -->
                            <button @click="scrollLeft(team)" :disabled="!canScrollLeft(team)"
                                class="matches-scroll-btn matches-scroll-left"
                                :class="{ 'disabled': !canScrollLeft(team) }" type="button"
                                aria-label="Прокрутить влево">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </button>

                            <!-- Квадратики с матчами -->
                            <div class="matches-row">
                                <template v-for="game in getVisibleGames(team)" :key="game.id">
                                    <div class="match-square" :class="{
                                        'match-win': game.is_finished && game.won,
                                        'match-loss': game.is_finished && !game.won,
                                        'match-upcoming': !game.is_finished,
                                        'match-no-stats': game.is_finished && !game.has_stats
                                    }" @click="handleMatchClick($event, game)"></div>
                                </template>

                                <!-- Заполнители, если матчей меньше 10 -->
                                <div v-for="n in (10 - getVisibleGames(team).length)" :key="`empty-${n}`"
                                    class="match-square match-empty"></div>
                            </div>

                            <!-- Стрелка вправо (всегда видима, но disabled когда нельзя прокрутить) -->
                            <button @click="scrollRight(team)" :disabled="!canScrollRight(team)"
                                class="matches-scroll-btn matches-scroll-right"
                                :class="{ 'disabled': !canScrollRight(team) }" type="button"
                                aria-label="Прокрутить вправо">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </td>
                </tr>
                <tr v-if="!standings.length" class="empty-row">
                    <td colspan="8">Данные загружаются...</td>
                </tr>
            </tbody>
        </table>
    </div>


    <!-- Tooltip из компонента -->
    <MatchTooltip v-if="hoveredGame" :games="[hoveredGame]" :position="tooltipPosition"
        :positionClass="tooltipPositionClass" />
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import MatchTooltip from '@/components/shared/MatchTooltip.vue';
import { formatSetsScore, formatRatio } from '@/utils/matchHelpers';
import { teamPublicPath } from '@/utils/teamUrl';

const props = defineProps({
    standings: {
        type: Array,
        default: () => []
    }
});

const maxPosition = computed(() => {
    if (!props.standings.length) {
        return null;
    }

    return Math.max(...props.standings.map((team) => team.position));
});

const hoveredGame = ref(null);
const tooltipPosition = ref({ x: 0, y: 0 });
const tooltipPositionClass = ref('top');
let hideTooltipTimeout = null;

// Индекс начала видимой области для каждой команды
const visibleStartIndex = ref({});

// Получить видимые матчи для команды (5 последних + 5 будущих)
const getVisibleGames = (team) => {
    if (!team.all_games || team.all_games.length === 0) {
        return [];
    }

    const allGames = team.all_games;

    // Находим индекс разделения между сыгранными и будущими матчами
    let currentIndex = allGames.findIndex(game => !game.is_finished);

    // Если все матчи сыграны
    if (currentIndex === -1) {
        currentIndex = allGames.length;
    }
    // Общее число доступных матчей и желаемое окно
    const total = allGames.length;
    const windowSize = Math.min(10, total);

    // Считаем, сколько сыгранных (в прошлом) и будущих матчей
    const pastCount = currentIndex; // сыгранные до текущего индекса
    const futureCount = total - currentIndex; // начиная с currentIndex — будущие

    // По умолчанию показываем до 5 справа (будущих) и до 5 слева (прошлых).
    // Если справа меньше 5, показываем все справа и заполняем слева до windowSize.
    const right = Math.min(5, futureCount);
    const left = Math.min(windowSize - right, pastCount);

    const start = Math.max(0, currentIndex - left);
    const end = Math.min(total, start + windowSize);

    // Инициализация позиции прокрутки при первом рендере для команды
    if (visibleStartIndex.value[team.id] === undefined) {
        visibleStartIndex.value[team.id] = start;
    }

    // Возвращаем срез, учитывающий пользовательскую прокрутку (visibleStartIndex),
    // но ограничиваем его корректными границами.
    const visibleStart = Math.max(0, Math.min(visibleStartIndex.value[team.id], Math.max(0, total - windowSize)));
    const visibleEnd = Math.min(total, visibleStart + windowSize);

    return allGames.slice(visibleStart, visibleEnd);
};

// Проверка, можно ли прокрутить влево
const canScrollLeft = (team) => {
    if (!team.all_games || team.all_games.length === 0) return false;
    const currentStart = visibleStartIndex.value[team.id] || 0;
    return currentStart > 0;
};

// Проверка, можно ли прокрутить вправо
const canScrollRight = (team) => {
    if (!team.all_games || team.all_games.length === 0) return false;
    const currentStart = visibleStartIndex.value[team.id] || 0;
    return currentStart + 10 < team.all_games.length;
};

// Прокрутка влево
const scrollLeft = (team) => {
    if (!canScrollLeft(team)) return;
    const currentStart = visibleStartIndex.value[team.id] || 0;
    visibleStartIndex.value[team.id] = Math.max(0, currentStart - 1);
};

// Прокрутка вправо
const scrollRight = (team) => {
    if (!canScrollRight(team)) return;
    const currentStart = visibleStartIndex.value[team.id] || 0;
    const maxStart = Math.max(0, team.all_games.length - 10);
    visibleStartIndex.value[team.id] = Math.min(maxStart, currentStart + 1);
};


// Открывает tooltip по клику на квадратик
const handleMatchClick = (event, game) => {
    if (!game) return;

    if (hideTooltipTimeout) {
        clearTimeout(hideTooltipTimeout);
        hideTooltipTimeout = null;
    }

    hoveredGame.value = game;
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 350;

    let x = rect.left + rect.width / 2;
    let y = rect.top - 10;
    let positionClass = 'top';

    if (x - tooltipWidth / 2 < 10) {
        x = tooltipWidth / 2 + 10;
    } else if (x + tooltipWidth / 2 > window.innerWidth - 10) {
        x = window.innerWidth - tooltipWidth / 2 - 10;
    }

    if (y < 100) {
        positionClass = 'bottom';
        y = rect.bottom + 10;
    }

    tooltipPosition.value = { x, y };
    tooltipPositionClass.value = positionClass;
};

const onDocumentClick = (e) => {
    const t = e.target;
    if (t.closest && (t.closest('.match-tooltip') || t.closest('.match-square') || t.closest('.match-result'))) {
        return;
    }
    hoveredGame.value = null;
};

onMounted(() => {
    document.addEventListener('click', onDocumentClick);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick);
});

// using shared helpers (imported above)
</script>

<style scoped>
.col-wins {
    color: var(--color-success);
}

.col-losses {
    color: var(--color-danger);
}

.win-number {
    color: var(--color-success);
    font-weight: 600;
}

.loss-number {
    color: var(--color-danger);
    font-weight: 600;
}

.sets-won {
    color: var(--color-success);
    font-weight: 600;
}

.sets-lost {
    color: var(--color-danger);
    font-weight: 600;
}

.sets-sep {
    margin: 0 4px;
    color: inherit;
}

.sets-score {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.sets-ratio {
    color: var(--color-text);
    display: block;
    text-align: center;
    margin-top: 4px;
    font-size: 0.75em;
    font-weight: 500;
}
</style>
